import React, { useEffect, useState } from "react";
import {
    Box,
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Menu,
    MenuItem,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import StopIcon from "@mui/icons-material/Stop";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PersonIcon from "@mui/icons-material/Person";
import { useHistory, useLocation } from "react-router-dom";
import dayjs from "dayjs";

import { Colors } from "../../../Styles/Colors";
import RestAPIService from "../../../Utility/Services/RestAPIService";
import { Serviceurls } from "../../../Utility/API/Serviceurls";
import ToastMsg from "../../../Components/ToastMsg/ToastMsg";
import Loader from "../../../Components/Loader";
import { ImagePaths } from "../../../Utility/ImagePaths";
import { CheckCircleOutline } from "@mui/icons-material";

export default function MedicinePreparation() {
    const history = useHistory();
    const location = useLocation();
    const walkinId = location.state?.id;
    const taskType = location.state?.row?.task_type;
    const taskId = location.state?.id;
    const isTreatmentTask = taskType === "Treatment Task" && taskId != null;

    const [treatmentData, setTreatmentData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [errorMsg, setErrorMsg] = useState(false);
    const [errorMsgText, setErrorMsgText] = useState("");
    const [successMsg, setSuccessMsg] = useState(false);
    const [successMsgText, setSuccessMsgText] = useState("");
    const [checklistOpen, setChecklistOpen] = useState(false);
    const [checklistData, setChecklistData] = useState({ Materials: [], Equipments: [] });
    const [checklistChecked, setChecklistChecked] = useState({});
    const [treatmentStates, setTreatmentStates] = useState({});
    const [batchSelection, setBatchSelection] = useState({});
    const [batchListByDrug, setBatchListByDrug] = useState({}); // key: treatmentId-drugId, value: batch_details from API [{ batch_no, stock_in_quantity, expiry_date, ... }]
    const [batchMenuAnchor, setBatchMenuAnchor] = useState(null);
    const [batchMenuKey, setBatchMenuKey] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    const row = location.state?.row || {};
    const patientName = row.patient_name || treatmentData?.patient_name || "-";
    const patientId = row.patient_account_number || treatmentData?.patient_account_number || "-";
    const patientPhone = row.patient_mobile_number || row.patient_mobile_number || "-";
    const consultantName = row.doctor_name || treatmentData?.consultant_name || "-";

    useEffect(() => {
        if (isTreatmentTask || walkinId) {
            fetchTreatment();
        } else {
            setErrorMsgText("Invalid appointment or task");
            setErrorMsg(true);
            setLoading(false);
        }
    }, [walkinId, isTreatmentTask, taskId]);

    const getRequiredQty = (drug) => {
        const q = drug?.quantity;
        if (q == null || q === "") return 0;
        const n = Number(q);
        if (!Number.isNaN(n)) return n;
        const parsed = parseFloat(String(q).replace(/[^\d.]/g, ""));
        return Number.isNaN(parsed) ? 0 : parsed;
    };

    const getBatchListForLine = (treatmentId, drugLineId) => {
        const key = `${treatmentId}-${drugLineId}`;
        return batchListByDrug[key] ?? [];
    };

    const getBatchQty = (batch) => Number(batch?.stock_in_quantity ?? batch?.available_qty ?? 0);

    const formatExpiryDate = (dateStr) => {
        if (!dateStr) return "-";
        const d = dayjs(dateStr);
        return d.isValid() ? d.format("DD-MM-YYYY") : "-";
    };

    const openBatchMenu = (e, key) => {
        setBatchMenuAnchor(e.currentTarget);
        setBatchMenuKey(key);
    };

    const closeBatchMenu = () => {
        setBatchMenuAnchor(null);
        setBatchMenuKey(null);
    };

    const renderBatchSelector = (treatmentId, drugLineId, drugOrComb) => {
        const key = `${treatmentId}-${drugLineId}`;
        const list = getBatchListForLine(treatmentId, drugLineId) || [];
        const selected = batchSelection[key] || "";
        const selectedBatch = list.find((b) => (b.batch_no ?? b.batch_id) === selected);
        const hasError = isBatchError(treatmentId, drugLineId, drugOrComb);
        return (
            <Box sx={{ minWidth: 150 }}>
                <Box
                    onClick={(e) => openBatchMenu(e, key)}
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        border: "1px solid",
                        borderColor: hasError ? "red" : "#ccc",
                        borderRadius: 1,
                        px: 1,
                        py: 0.75,
                        cursor: "pointer",
                        backgroundColor: "#fff",
                        "&:hover": { backgroundColor: "#fafafa" },
                        ...(hasError && { borderWidth: 2 }),
                    }}
                >
                    <Typography fontSize="0.8vw" color={selected ? "textPrimary" : "textSecondary"}>
                        {selected ? (selectedBatch?.batch_no ?? selected) : "Select Batch"}
                    </Typography>
                    <ArrowDropDownIcon fontSize="small" sx={{ color: "#666" }} />
                </Box>
                <Menu
                    anchorEl={batchMenuAnchor}
                    open={batchMenuKey === key}
                    onClose={closeBatchMenu}
                    anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                    transformOrigin={{ vertical: "top", horizontal: "left" }}
                    PaperProps={{ sx: { maxHeight: 320, minWidth: 250 } }}
                >
                    <Box sx={{ px: 1, py: 0.5, borderBottom: "1px solid #e0e0e0", backgroundColor: Colors.ThemeLightColor || "#f5f5f5" }}>
                        <Box sx={{ display: "flex", flexDirection: "row", width: "100%" }}>
                            <Typography width="35%" fontWeight={600} fontSize="0.8vw">Batch No</Typography>
                            <Typography width="35%" fontWeight={600} fontSize="0.8vw">Expiry Date</Typography>
                            <Typography width="30%" fontWeight={600} fontSize="0.8vw">Avl/Qty</Typography>
                        </Box>
                    </Box>
                    <Box sx={{ maxHeight: 240, overflow: "auto" }}>
                        {list.length === 0 ? (
                            <Typography fontSize="0.8vw" color="textSecondary" sx={{ py: 1.5, px: 1, textAlign: "center" }}>No Batches Found</Typography>
                        ) : (
                            list.map((b, idx) => (
                                <MenuItem
                                    key={b.batch_no ?? b.batch_id ?? idx}
                                    onClick={() => {
                                        setBatchSelection((prev) => ({ ...prev, [key]: b.batch_no ?? b.batch_id }));
                                        closeBatchMenu();
                                    }}
                                    sx={{ display: "flex", flexDirection: "row", py: 0.75 }}
                                >
                                    <Typography width="35%" fontSize="0.8vw" noWrap>{b.batch_no ?? b.batch_id ?? "-"}</Typography>
                                    <Typography width="35%" fontSize="0.8vw">{formatExpiryDate(b.expiry_date)}</Typography>
                                    <Typography width="30%" fontSize="0.8vw">{getBatchQty(b)}</Typography>
                                </MenuItem>
                            ))
                        )}
                    </Box>
                </Menu>
            </Box>
        );
    };

    const isBatchError = (treatmentId, drugLineId, drugOrComb) => {
        const list = getBatchListForLine(treatmentId, drugLineId);
        const required = getRequiredQty(drugOrComb);
        const selected = batchSelection[`${treatmentId}-${drugLineId}`];
        if (!list || list.length === 0) return true;
        if (!selected) return true;
        const batch = list.find((b) => (b.batch_no ?? b.batch_id) === selected);
        if (!batch) return true;
        const avail = getBatchQty(batch);
        return avail < required;
    };

    useEffect(() => {
        if (!treatmentData?.treatments) return;
        const listByDrug = {};
        const initialSelection = {};
        (treatmentData.treatments || []).forEach((treatment) => {
            const drugs = treatment.drugs || [];
            drugs.forEach((drug) => {
                const key = `${treatment.id}-${drug.id}`;
                const batchDetails = drug.batch_details ?? [];
                listByDrug[key] = batchDetails;
                const required = getRequiredQty(drug);
                const firstMeeting = batchDetails.find((b) => getBatchQty(b) >= required);
                const first = batchDetails[0];
                if (firstMeeting) initialSelection[key] = firstMeeting.batch_no ?? firstMeeting.batch_id;
                else if (first) initialSelection[key] = first.batch_no ?? first.batch_id;
            });
            drugs.forEach((drug) => {
                (drug.combinations || []).forEach((comb) => {
                    const key = `${treatment.id}-${comb.id}`;
                    const batchDetails = comb.batch_details ?? [];
                    listByDrug[key] = batchDetails;
                    const required = getRequiredQty(comb);
                    const firstMeeting = batchDetails.find((b) => getBatchQty(b) >= required);
                    const first = batchDetails[0];
                    if (firstMeeting) initialSelection[key] = firstMeeting.batch_no ?? firstMeeting.batch_id;
                    else if (first) initialSelection[key] = first.batch_no ?? first.batch_id;
                });
            });
        });
        setBatchListByDrug((prev) => ({ ...prev, ...listByDrug }));
        setBatchSelection((prev) => ({ ...prev, ...initialSelection }));
    }, [treatmentData]);

    const fetchTreatment = () => {
        setLoading(true);
        const getUrl = isTreatmentTask
            ? `${Serviceurls.AYURVEDA_MEDICINE_PREPARATION_TREATMENT}?task_type=Treatment Task&id=${taskId}`
            : `${Serviceurls.AYURVEDA_MEDICINE_PREPARATION_TREATMENT}?id=${walkinId}`;
        RestAPIService.getAll(getUrl)
            .then((res) => {
                if (res?.data?.status === "success" && res?.data?.data) {
                    const raw = res.data.data;
                    let data = raw;
                    if (isTreatmentTask && raw && !raw.treatments && (raw.drugs || raw.task_name)) {
                        data = {
                            ...raw,
                            treatments: [{
                                id: raw.id,
                                treatment_name: raw.task_name || "Treatment Task",
                                drugs: raw.drugs || [],
                                preparation_status: raw.preparation_status || "Not Started",
                                medication_start_time: raw.medication_start_time,
                                medication_end_time: raw.medication_end_time,
                            }],
                        };
                    }
                    setTreatmentData(data);
                    const initial = {};
                    (data?.treatments || []).forEach((t) => {
                        initial[t.id] = { preparation_status: t.preparation_status || "Not Started", medication_start_time: t.medication_start_time, medication_end_time: t.medication_end_time };
                    });
                    setTreatmentStates(initial);
                } else {
                    setErrorMsgText(res?.data?.message || "Failed to load");
                    setErrorMsg(true);
                }
            })
            .catch((e) => {
                setErrorMsgText(e?.response?.data?.message || e.message || "Failed to load");
                setErrorMsg(true);
            })
            .finally(() => setLoading(false));
    };

    const fetchChecklist = () => {
        const checklistId = isTreatmentTask ? taskId : walkinId;
        const url = isTreatmentTask ? Serviceurls.AYURVEDA_THERAPY_CHECKLIST + "?id=" + checklistId + "&task_type=Treatment Task" : Serviceurls.AYURVEDA_THERAPY_CHECKLIST + "?id=" + checklistId;
        RestAPIService.getAll(url)
            .then((res) => {
                if (res?.data?.status === "success" && res?.data?.data) {
                    const data = res.data.data;
                    setChecklistData({
                        Materials: data.Materials || [],
                        Equipments: data.Equipments || [],
                    });
                    const checked = {};
                    (data.Equipments || []).forEach((e) => { checked[`eq-${e.id}`] = true; });
                    (data.Materials || []).forEach((m) => { checked[`med-${m.id}`] = true; });
                    setChecklistChecked(checked);
                    setChecklistOpen(true);
                } else {
                    setErrorMsgText(res?.data?.message || "Failed to load checklist");
                    setErrorMsg(true);
                }
            })
            .catch((e) => {
                setErrorMsgText(e?.response?.data?.message || e.message || "Failed to load checklist");
                setErrorMsg(true);
            });
    };

    const handleStatusUpdate = (payload) => {
        setSubmitting(true);
        RestAPIService.updateWithOutId(payload, Serviceurls.AYURVEDA_WALKIN_APPOINTMENT_STATUS_UPDATE)
            .then((res) => {
                if (res?.data?.status === "success") {
                    setSuccessMsgText(res?.data?.message || "Updated");
                    setSuccessMsg(true);
                    fetchTreatment();
                } else {
                    setErrorMsgText(res?.data?.message || "Update failed");
                    setErrorMsg(true);
                }
            })
            .catch((e) => {
                setErrorMsgText(e?.response?.data?.message || e.message || "Update failed");
                setErrorMsg(true);
            })
            .finally(() => setSubmitting(false));
    };

    /** Build drugs array for Start API: each drug/comb line with selected batch (id, batch_no, mrp_per_quantity, stock_id, quantity, uom). */
    const buildDrugsPayloadForTreatment = (treatment) => {
        const drugs = treatment.drugs || [];
        const out = [];
        drugs.forEach((drug) => {
            const key = `${treatment.id}-${drug.id}`;
            const list = getBatchListForLine(treatment.id, drug.id);
            const selected = batchSelection[key];
            const batch = list?.find((b) => (b.batch_no ?? b.batch_id) === selected);
            out.push({
                id: drug.id,
                batch_no: batch?.batch_no ?? batch?.batch_id ?? "",
                mrp_per_quantity: batch?.mrp_per_quantity ?? batch?.mrp ?? 0,
                stock_id: batch?.stock_id ?? batch?.id ?? null,
                quantity: Number(drug.quantity) || 0,
                uom: drug.uom || "nos",
            });
            (drug.combinations || []).forEach((comb) => {
                const cKey = `${treatment.id}-${comb.id}`;
                const cList = getBatchListForLine(treatment.id, comb.id);
                const cSelected = batchSelection[cKey];
                const cBatch = cList?.find((b) => (b.batch_no ?? b.batch_id) === cSelected);
                out.push({
                    id: comb.id,
                    batch_no: cBatch?.batch_no ?? cBatch?.batch_id ?? "",
                    mrp_per_quantity: cBatch?.mrp_per_quantity ?? cBatch?.mrp ?? 0,
                    stock_id: cBatch?.stock_id ?? cBatch?.id ?? null,
                    quantity: Number(comb.quantity) || 0,
                    uom: comb.uom || "nos",
                });
            });
        });
        return out;
    };

    /** Start button: PUT with preparation_status "Started" and drugs array (batch update). */
    const handleStart = (treatment) => {
        const drugs = buildDrugsPayloadForTreatment(treatment);
        const payload = isTreatmentTask
            ? { task_type: "Treatment Task", task_id: Number(taskId), preparation_status: "Started", drugs }
            : { walkin_appointment_id: Number(walkinId) , treatment_plan_id: treatment.id, preparation_status: "Started", drugs };
        handleStatusUpdate(payload);
    };

    /** Stop button (after Start only): PUT with preparation_status "Completed", no drugs. */
    const handleStop = (treatment) => {
        const payload = isTreatmentTask
            ? { task_type: "Treatment Task", task_id: Number(taskId), preparation_status: "Completed" }
            : { walkin_appointment_id: Number(walkinId) , treatment_plan_id: treatment.id, preparation_status: "Completed" };
        handleStatusUpdate(payload);
        fetchTreatment();
    };


    const onCompleteClick = () => {
        fetchChecklist();
    };

    const onChecklistComplete = () => {
        setSubmitting(true);
        const postPayload = isTreatmentTask
            ? { task_type: "Treatment Task", task_id: Number(taskId), status: "Medication Ready" }
            : { id: walkinId, status: "Medication Ready" };
        RestAPIService.create(postPayload, Serviceurls.AYURVEDA_WALKIN_APPOINTMENT_STATUS_UPDATE)
            .then((res) => {
                if (res?.data?.status === "success") {
                    setChecklistOpen(false);
                    setSuccessMsgText(res?.data?.message || "Medication ready for therapy.");
                    setSuccessMsg(true);
                    setTimeout(() => {
                        history.push("/AyurvedaMedication");
                    }, 2000);
                } else {
                    setErrorMsgText(res?.data?.message || "Complete failed");
                    setErrorMsg(true);
                }
            })
            .catch((e) => {
                setErrorMsgText(e?.response?.data?.message || e.message || "Complete failed");
                setErrorMsg(true);
            })
            .finally(() => setSubmitting(false));
    };

    const getTreatmentStatus = (treatment) => {
        return treatmentStates[treatment.id]?.preparation_status || treatment.preparation_status || "Not Started";
    };

    const canStartTreatment = (treatment) => {
        const drugs = treatment.drugs || [];
        for (const drug of drugs) {
            if (isBatchError(treatment.id, drug.id, drug)) return false;
            for (const comb of drug.combinations || []) {
                if (isBatchError(treatment.id, comb.id, comb)) return false;
            }
        }
        return true;
    };

    const goBack = () => history.push("/AyurvedaMedication");

    if (loading && !treatmentData) {
        return <Loader />;
    }

    const treatments = treatmentData?.treatments || [];
    const allTreatmentsCompleted = treatments.length > 0 && treatments.every((t) => {
        const s = getTreatmentStatus(t);
        return s === "Completed" || s === "Stopped";
    });

    return (
        <Box sx={{ height: "85vh", width: "100%", backgroundColor: Colors.Background, p: 1, overflow: "auto" }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", backgroundColor: "white", p: 1, borderBottom: "1px solid #eee" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <IconButton size="small" onClick={goBack} sx={{ color: "#4da3d4" }}>
                        <ChevronLeftIcon />
                    </IconButton>
                    <Typography color="textSecondary" fontWeight={600} fontSize="0.95vw">
                        Medicine Preparation
                    </Typography>
                </Box>
            </Box>

            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", backgroundColor: "white", p: 1.5, mt: 1, flexWrap: "wrap", gap: 1 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Box sx={{ width: 40, height: 40, borderRadius: "50%", overflow: "hidden", bgcolor: "#e0e0e0" }}>
                        <img src={row.photo_url || ImagePaths.Male.default} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                    </Box>
                    <Box>
                        <Typography fontSize="0.9vw" fontWeight={500}>{patientName} {row.patient_age ? `${row.patient_age}/y` : ""}</Typography>
                        <Typography fontSize="0.75vw" color="textSecondary">{patientId} | {patientPhone}</Typography>
                    </Box>
                </Box>
                <Button
                    variant="contained"
                    disabled={submitting || !allTreatmentsCompleted}
                    onClick={onCompleteClick}
                    startIcon={<CheckCircleIcon />}
                    sx={{
                        bgcolor: allTreatmentsCompleted ? "#2e7d32" : "#9e9e9e",
                        "&:hover": { bgcolor: allTreatmentsCompleted ? "#1b5e20" : "#757575" },
                        textTransform: "uppercase",
                        fontSize: "0.8vw",
                    }}
                >
                    Complete
                </Button>
            </Box>

            <Box sx={{ backgroundColor: "white", mt: 1, p: 1 }}>
                <Typography fontWeight={600} fontSize="0.9vw" sx={{ mb: 1 }}>Medication Preparation</Typography>
                <TableContainer>
                    <Table size="small" sx={{ "& .MuiTableCell-root": { fontSize: "0.8vw", border: "1px solid #e0e0e0" } }}>
                        <TableHead>
                            <TableRow sx={{ bgcolor: Colors.Primary}}>
                                <TableCell sx={{ verticalAlign: "top", fontWeight: 600, color: "white" }}>Treatment Name</TableCell>
                                <TableCell sx={{ verticalAlign: "top", fontWeight: 600, color: "white" }}>Drug Name</TableCell>
                                <TableCell sx={{ verticalAlign: "top", fontWeight: 600, color: "white" }}>UOM</TableCell>
                                <TableCell sx={{ verticalAlign: "top", fontWeight: 600, color: "white" }}>Batch</TableCell>
                                <TableCell sx={{ verticalAlign: "top", fontWeight: 600, color: "white" }}>Start Time</TableCell>
                                <TableCell sx={{ verticalAlign: "top", fontWeight: 600, color: "white" }}>End Time</TableCell>
                                <TableCell sx={{ verticalAlign: "top", fontWeight: 600, color: "white" }}>Instruction</TableCell>
                                <TableCell sx={{ verticalAlign: "top", fontWeight: 600, color: "white" }} align="center">Action</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {treatments.map((treatment, tIdx) => {
                                const status = getTreatmentStatus(treatment);
                                const state = treatmentStates[treatment.id] || {};
                                const drugs = treatment.drugs || [];
                                return (
                                    <React.Fragment key={treatment.id}>
                                        {drugs.map((drug, dIdx) => {
                                            // Color mapping same as DocPrescription: alternate per drug group (single or combo = one group)
                                            const drugGroupIndex = treatments
                                                .slice(0, tIdx)
                                                .reduce((acc, t) => acc + (t.drugs || []).length, 0) + dIdx;
                                            const borderColor = drugGroupIndex % 2 === 0 ? "gray" : "#4da3d4";
                                            const rowBg = drugGroupIndex % 2 === 0 ? "#fafafa" : "#d7ecff";
                                            const rowSpan = drugs.reduce((acc, d) => acc + 1 + (d.combinations?.length || 0), 0);
                                            return (
                                                <React.Fragment key={drug.id}>
                                                    <TableRow sx={{ bgcolor: rowBg }}>
                                                        {dIdx === 0 ? (
                                                            <TableCell rowSpan={rowSpan} sx={{ verticalAlign: "top", pl: 1 }}>
                                                                <Typography fontWeight={500}>{treatment.treatment_name} -{String(tIdx + 1).padStart(2, "0")}</Typography>
                                                                <Typography fontSize="0.7vw" color="textSecondary">{status}</Typography>
                                                            </TableCell>
                                                        ) : null}
                                                        <TableCell sx={{ verticalAlign: "top", pl: 1, borderLeft: `5px solid ${borderColor}` }}>
                                                            <Typography fontSize="0.8vw">{drug.drug_name}</Typography>
                                                        </TableCell>
                                                        <TableCell sx={{ verticalAlign: "top" }}>
                                                            <Typography fontSize="0.8vw">{drug.quantity} {drug.uom}</Typography>
                                                        </TableCell>
                                                        <TableCell sx={{ verticalAlign: "top" }}>
                                                            {renderBatchSelector(treatment.id, drug.id, drug)}
                                                        </TableCell>
                                                        {dIdx === 0 ? (
                                                            <TableCell rowSpan={rowSpan} sx={{ verticalAlign: "top" }}>{state.medication_start_time ? dayjs(state.medication_start_time).format("hh:mm A") : "-"}</TableCell>
                                                        ) : null}
                                                        {dIdx === 0 ? (
                                                            <TableCell rowSpan={rowSpan} sx={{ verticalAlign: "top" }}>{state.medication_end_time ? dayjs(state.medication_end_time).format("hh:mm A") : "-"}</TableCell>
                                                        ) : null}
                                                        {dIdx === 0 ? (
                                                            <TableCell rowSpan={rowSpan} sx={{ verticalAlign: "top" }}>{treatmentData?.instruction || "-"}</TableCell>
                                                        ) : null}
                                                        {dIdx === 0 ? (
                                                            <TableCell rowSpan={rowSpan} sx={{ verticalAlign: "top" }} align="center">
                                                                {(status === "Not Started") && (
                                                                    <IconButton
                                                                        size="small"
                                                                        color="primary"
                                                                        onClick={() => handleStart(treatment)}
                                                                        title="Start"
                                                                        disabled={!canStartTreatment(treatment)}
                                                                    >
                                                                        <PlayArrowIcon />
                                                                    </IconButton>
                                                                )}
                                                                {status === "Started" && (
                                                                    <IconButton size="small" color="error" onClick={() => handleStop(treatment)} title="Stop">
                                                                        <StopIcon />
                                                                    </IconButton>
                                                                )}
                                                                {(status === "Stopped" || status === "Completed") && (
                                                                    <IconButton size="small" color="success" title="Completed">
                                                                    <CheckCircleOutline />
                                                                </IconButton>
                                                                                                                                    )}
                                                            </TableCell>
                                                        ) : null}
                                                    </TableRow>
                                                    {(drug.combinations || []).map((comb) => (
                                                        <TableRow key={comb.id} sx={{ bgcolor: rowBg }}>
                                                            <TableCell sx={{ verticalAlign: "top", pl: 1.5, borderLeft: `5px solid ${borderColor}` }}>
                                                                <Box sx={{ borderLeft: "3px solid rgba(0,0,0,0.12)", pl: 1 }}>
                                                                    <Typography fontSize="0.8vw">{comb.drug_name}</Typography>
                                                                </Box>
                                                            </TableCell>
                                                            <TableCell sx={{ verticalAlign: "top" }}>
                                                                <Typography fontSize="0.8vw">{comb.quantity} {comb.uom}</Typography>
                                                            </TableCell>
                                                            <TableCell sx={{ verticalAlign: "top" }}>
                                                                {renderBatchSelector(treatment.id, comb.id, comb)}
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                </React.Fragment>
                                            );
                                        })}
                                    </React.Fragment>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>

            <Box sx={{ backgroundColor: "white", mt: 1, p: 1 }}>
                <Typography fontWeight={600} fontSize="0.9vw" sx={{ mb: 0.5 }}>Notes</Typography>
                <Typography fontSize="0.8vw" color="textSecondary">{treatmentData?.instruction || "-"}</Typography>
            </Box>

            <Dialog open={checklistOpen} onClose={() => setChecklistOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle>Checklist - Ayurveda Therapy</DialogTitle>
                <DialogContent>
                    <Stack direction="row" spacing={4} sx={{ pt: 1 }}>
                        <Box sx={{ flex: 1 }}>
                            <Typography fontWeight={600} sx={{ mb: 1 }}>EQUIPMENTS</Typography>
                            {(checklistData?.Equipments || []).map((item) => (
                                <Box key={item.id} sx={{ display: "flex", alignItems: "center", py: 0.5 }}>
                                    <Checkbox
                                        checked={checklistChecked[`eq-${item.id}`] ?? true}
                                        onChange={(e) => setChecklistChecked((prev) => ({ ...prev, [`eq-${item.id}`]: e.target.checked }))}
                                        size="small"
                                    />
                                    <Typography fontSize="0.9vw">{item.name}</Typography>
                                </Box>
                            ))}
                        </Box>
                        <Box sx={{ flex: 1 }}>
                            <Typography fontWeight={600} sx={{ mb: 1 }}>MATERIALS</Typography>
                            {(checklistData?.Materials || []).map((item) => (
                                <Box key={item.id} sx={{ display: "flex", alignItems: "center", py: 0.5 }}>
                                    <Checkbox
                                        checked={checklistChecked[`med-${item.id}`] ?? true}
                                        onChange={(e) => setChecklistChecked((prev) => ({ ...prev, [`med-${item.id}`]: e.target.checked }))}
                                        size="small"
                                    />
                                    <Typography fontSize="0.9vw">{item.name}</Typography>
                                </Box>
                            ))}
                        </Box>
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setChecklistOpen(false)}>Cancel</Button>
                    <Button variant="contained" color="primary" onClick={onChecklistComplete} disabled={submitting}>
                        Complete
                    </Button>
                </DialogActions>
            </Dialog>

            {errorMsg && <ToastMsg severity="error" msg={errorMsgText} msgPop={() => { setErrorMsg(false); setErrorMsgText(""); }} />}
            {successMsg && <ToastMsg severity="success" msg={successMsgText} msgPop={() => { setSuccessMsg(false); setSuccessMsgText(""); }} />}
            {submitting && <Loader />}
        </Box>
    );
}
